import { getApiClient } from '../api-client';
import { getData, getPagination } from '../api-response';
import { buildListQuery, type ListQueryParams } from '../query';

// Use dynamic API client that switches based on current environment
const apiClient = getApiClient();

export type UserTheme = 'LIGHT' | 'DARK';

export interface User {
  id: string;
  name: string;
  email: string;
  company?: string;
  username?: string | null;
  theme?: UserTheme;
  role: string;
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
  isActive: boolean;
  apiKeyCount?: number;
}

export interface AllUsersCount {
  count: number;
}

export interface UpdateUserData {
  name?: string;
  company?: string;
  username?: string | null;
  theme?: UserTheme;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface UpdateRoleData {
  role: string;
}

export interface UserResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AvailableRoles {
  roles: string[];
  descriptions: Record<string, string>;
}

export class UserApiService {
  // Get all users count
  static async getAllUsersCount(): Promise<AllUsersCount> {
    const response = await apiClient.get('/user/all/count');
    return getData(response);
  }

  // Get user profile
  static async getProfile(): Promise<User> {
    const response = await apiClient.get('/user/profile');
    return getData(response);
  }

  // Update user profile
  static async updateProfile(data: UpdateUserData): Promise<User> {
    const response = await apiClient.put('/user/profile', data);
    return getData(response);
  }

  // Change password
  static async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    const response = await apiClient.post('/user/change-password', data);
    return getData(response);
  }

  // Get all users (admin only)
  static async getAllUsers(params: ListQueryParams | number = 1, limit = 50): Promise<UserResponse> {
    const query =
      typeof params === 'number'
        ? { page: params, limit }
        : { page: 1, limit: 10, ...params };
    const response = await apiClient.get(`/user/all${buildListQuery(query)}`);
    const data = getData(response);
    return {
      users: Array.isArray(data) ? data : [],
      pagination: getPagination(response)!,
    };
  }

  // Get user by ID
  static async getUserById(id: string): Promise<User> {
    const response = await apiClient.get(`/user/${id}`);
    return getData(response);
  }

  // Update user role (admin only)
  static async updateUserRole(id: string, role: string): Promise<User> {
    const response = await apiClient.post(`/user/${id}/role`, { role });
    return getData(response);
  }

  // Get available roles
  static async getAvailableRoles(): Promise<AvailableRoles> {
    const response = await apiClient.get('/user/roles/available');
    return getData(response);
  }
}

export default UserApiService;
